import { TFunction } from "i18next";
import capitalize from "lodash/capitalize";
import snakeCase from "lodash/snakeCase";

import {
  Avatar,
  AvatarProps,
  ChipState,
  Icon,
  IconName,
  NestedDataCardProps,
  SingleStatusChip,
  createDataCardInitialColumnDefinition,
  createInfoCardColumnDefinition,
  formatDate,
} from "@inductiveautomation/ws-components";

import { addMonths } from "date-fns";
import { TestPricing, TrainingDetails } from "../api/training.api";
import { TranslateKey } from "../../../i18n/i18n.types";
import { getCurrentActions } from "../Training.actionsForState";
import {
  Certification,
  CertificationActionCallback,
  CertificationActionKind,
  CertificationType,
  ElectiveWithType,
  GetCertificationsResponse,
} from "../Training.types";
import { IconContainer } from "./IconContainer";

interface CurrentCertificationAttributes {
  card: NestedDataCardProps;
}

const dateColumn = (testId: string, date?: Date) => ({
  testId,
  content: date ? formatDate(date, "date") : "-",
  minWidth: 102,
});

const firstForKind = (
  certifications: Certification[],
  type: CertificationType
) => certifications.find((certification) => certification.type === type);

const labelForKey = (
  t: TFunction,
  type: CertificationType | "ELECTIVE" | "EMPLOYEE"
) => {
  if (type === "CREDENTIAL")
    return t("training.actions.get_credentialed.label");
  if (type === "CORE") return t("training.actions.get_core_certified.label");
  if (type === "GOLD") return t("training.actions.get_gold_certified.label");
  if (type === "ELECTIVE")
    return t("training.actions.get_started_with_elective_courses.label");

  return t("training.actions.get_started_with_employee_courses.label");
};

const ACTION_KEY: Record<
  CertificationType | "ELECTIVE" | "EMPLOYEE" | "CORE-FREE" | "GOLD-FREE",
  CertificationActionKind
> = {
  CREDENTIAL: "start-credential",
  CORE: "start-core-certification",
  "CORE-FREE": "purchase-test",
  GOLD: "start-gold-certification",
  "GOLD-FREE": "purchase-test",
  ELECTIVE: "start-elective-courses",
  EMPLOYEE: "start-employee-courses",
};

const retrieveAvatarProps = ({
  status,
  iconUrl,
}: {
  status: string;
  iconUrl: string;
}) => {
  if (status === "IN_PROGRESS" || status === "NOT_STARTED") {
    return {
      icon: "certification" as IconName,
      iconAvatarColor: "empty",
    } as AvatarProps;
  }

  return {
    imageUrl: iconUrl,
    variant: "image",
    imageUrlRemoveBorderRadius: true,
    iconAvatarColor: "empty",
  } as AvatarProps;
};

export const retrieveChipAttributes = ({
  t,
  status,
}: {
  t: TFunction;
  status: string;
}) => {
  if (status === "IN_PROGRESS") {
    return {
      state: "info" as ChipState,
      label: t("training.in_progress_chip"),
    };
  }

  if (status === "INVALID") {
    return {
      state: "warning" as ChipState,
      label: t("training.invalid_chip"),
    };
  }

  if (status === "EXPIRED") {
    return {
      state: "error" as ChipState,
      label: t("training.expired_chip"),
    };
  }

  if (status === "EXPIRES_SOON") {
    return {
      state: "warning" as ChipState,
      label: t("training.expires_soon_chip"),
    };
  }

  return {
    state: "neutral" as ChipState,
    label: t("training.earned_chip"),
  };
};

const expiresSoon = (expirationDate: string) => {
  const SOON_THRESHOLD_MONTHS = 6;
  const currentDate = new Date();

  return (
    expirationDate &&
    expirationDate < addMonths(currentDate, SOON_THRESHOLD_MONTHS).toISOString()
  );
};
export const getCredentialAttributes = (
  t: TFunction,
  trainingDetails: TrainingDetails | undefined,
  onAction: CertificationActionCallback
) => {
  if (
    !trainingDetails?.credential?.status ||
    trainingDetails.credential.status == "NOT_STARTED"
  ) {
    return {
      current: {
        card: emptyCard(t, "CREDENTIAL"),
      },
      footerProps: {
        buttonProps: {
          testId: "start-CREDENTIAL",
          label: labelForKey(t, "CREDENTIAL"),
          onClick: () => onAction(ACTION_KEY.CREDENTIAL),
          disabled: false,
        },
      },
    };
  }
  const { credential, credentialHistory } = trainingDetails;
  const certification = {
    earnDate: credential.dateEarned,
    id: "CREDENTIAL",
    name: t("training.credential_name", { versionNumber: credential.version }),
    status: credential.status,
    type: "CREDENTIAL" as CertificationType,
    history: credentialHistory?.map(({ dateEarned, version, iconUrl }) => {
      return {
        earnDate: dateEarned,
        version,
        iconUrl,
      };
    }),
    iconUrl: credential.iconUrl,
  };

  const testId = `certification-card-${credential.version.replace(".", "-")}_iu_credential`;
  const avatarProps = (
    credential.status === "EARNED"
      ? {
          imageUrl: credential.iconUrl,
          variant: "image",
          imageUrlRemoveBorderRadius: true,
        }
      : {
          icon: "credential" as IconName,
          iconAvatarColor: "empty",
        }
  ) as AvatarProps;

  return {
    current: {
      card: {
        id: "CREDENTIAL",
        testId,
        disabled: false,
        contentColumns: [
          createDataCardInitialColumnDefinition(
            {
              avatar: <Avatar {...avatarProps} />,
              parentTestId: testId,
              heading: certification.name,
              subtext: "",
            },
            undefined,
            256
          ),
          credential.dateEarned
            ? dateColumn(`${testId}-earned`, new Date(credential.dateEarned))
            : dateColumn(`${testId}-earned`),
          { content: " " },
          createInfoCardColumnDefinition(
            <SingleStatusChip
              testId={`${testId}-info-chip`}
              {...retrieveChipAttributes({ t, status: certification.status })}
            />,
            undefined,
            120
          ),
        ],
        actionLists: getCurrentActions(t, onAction, certification),
        childItems: [],
      },
      certification,
    },
    footerProps: {},
  };
};

export const getCoreAttributes = (
  t: TFunction,
  certifications: TrainingDetails["certifications"],
  onAction: CertificationActionCallback,
  corePricingData?: TestPricing
) => {
  if (!certifications || certifications.CORE.status === "UNEARNED") {
    return {
      current: {
        card: emptyCard(t, "CORE"),
      },
      footerProps: {
        buttonProps: {
          testId: "start-CORE",
          label: labelForKey(t, "CORE"),
          onClick: () =>
            onAction(
              corePricingData?.creditsAvailable?.length
                ? ACTION_KEY["CORE-FREE"]
                : ACTION_KEY.CORE,
              {
                certificationPurchaseType: "CORE",
              }
            ),
          disabled: false,
        },
      },
    };
  }

  const {
    earnMethod,
    history,
    testDeadline,
    testUrl,
    earnDate,
    expirationDate = "",
    version,
    status,
    iconUrl = "",
  } = certifications.CORE;

  const coreExpiresSoon = status === "EARNED" && expiresSoon(expirationDate);
  const goldExpiresSoon =
    certifications.GOLD.status === "EARNED" &&
    certifications.GOLD.expirationDate &&
    expiresSoon(certifications.GOLD.expirationDate);

  const certification = {
    earnMethod,
    earnDate,
    expirationDate,
    id: "CORE",
    name: t("training.core_name", { versionNumber: version }),
    status: coreExpiresSoon && !goldExpiresSoon ? "EXPIRES_SOON" : status,
    goldStatus: certifications.GOLD.status,
    type: "CORE" as CertificationType,
    testUrl,
    history,
    iconUrl,
    testDeadline,
  };
  const testId = `certification-card-${version?.replace(".", "-")}_iu_core`;

  // TS-1841 Grid action menu
  const actionLists = getCurrentActions(
    t,
    onAction,
    certification,
    corePricingData?.canPurchaseOnline
  );

  return {
    current: {
      card: {
        id: "CORE",
        testId,
        disabled: false,
        contentColumns: [
          createDataCardInitialColumnDefinition(
            {
              avatar: (
                <Avatar
                  {...retrieveAvatarProps({
                    status: certification.status,
                    iconUrl,
                  })}
                />
              ),
              parentTestId: testId,
              heading: certification.name,
              subtext: capitalize(earnMethod?.toLowerCase()),
            },
            undefined,
            256
          ),
          earnDate
            ? dateColumn(`${testId}-earned`, new Date(earnDate))
            : dateColumn(`${testId}-earned`),
          expirationDate
            ? // TS-1841 Grid expiration date
              dateColumn(
                `${testId}-expired`,
                certifications.GOLD.status === "EARNED" ||
                  certifications.GOLD.status === "EXPIRES_SOON"
                  ? undefined
                  : new Date(expirationDate)
              )
            : dateColumn(`${testId}-expired`),
          createInfoCardColumnDefinition(
            // TS-1841 Grid status badge
            certifications.GOLD.status === "EARNED" ||
              certifications.GOLD.status === "EXPIRES_SOON" ? (
              "-"
            ) : (
              <SingleStatusChip
                testId={`${testId}-info-chip`}
                {...retrieveChipAttributes({ t, status: certification.status })}
              />
            ),
            undefined,
            120
          ),
        ],
        actionLists,
        childItems: [],
        showDisabledMenuTrigger: actionLists.length == 0,
      },
      certification,
    },
    footerProps: {},
  };
};

export const getGoldAttributes = (
  t: TFunction,
  certifications: TrainingDetails["certifications"],
  onAction: CertificationActionCallback,
  goldPricingData?: TestPricing
) => {
  if (!certifications || certifications.GOLD.status === "UNEARNED") {
    return {
      current: {
        card: emptyCard(t, "GOLD"),
      },
      footerProps: {
        buttonProps: {
          testId: "start-GOLD",
          label: labelForKey(t, "GOLD"),
          onClick: () =>
            onAction(
              goldPricingData?.creditsAvailable?.length
                ? ACTION_KEY["GOLD-FREE"]
                : ACTION_KEY.GOLD,
              {
                certificationPurchaseType: "GOLD",
              }
            ),
          disabled: certifications?.CORE.status !== "EARNED",
        },
      },
    };
  }

  const {
    earnDate,
    history,
    testDeadline,
    earnMethod,
    expirationDate = "",
    version,
    status,
    iconUrl = "",
  } = certifications.GOLD;

  const testId = `certification-card-${version?.replace(".", "-")}_iu_gold`;
  const certification = {
    earnDate,
    earnMethod,
    expirationDate,
    id: "GOLD",
    name: t("training.gold_name", { versionNumber: version }),
    status:
      status === "EARNED" && expiresSoon(expirationDate)
        ? "EXPIRES_SOON"
        : status,
    type: "GOLD" as CertificationType,
    history,
    iconUrl,
    testDeadline,
  };
  const actionLists = getCurrentActions(
    t,
    onAction,
    certification,
    goldPricingData?.canPurchaseOnline
  );

  return {
    current: {
      card: {
        id: "GOLD",
        testId,
        disabled: false,
        contentColumns: [
          createDataCardInitialColumnDefinition(
            {
              avatar: (
                <Avatar
                  {...retrieveAvatarProps({
                    status: certification.status,
                    iconUrl,
                  })}
                />
              ),
              parentTestId: testId,
              heading: certification.name,
              subtext: capitalize(earnMethod?.toLowerCase()),
            },
            undefined,
            256
          ),
          earnDate
            ? dateColumn(`${testId}-earned`, new Date(earnDate))
            : dateColumn(`${testId}-earned`),
          expirationDate
            ? dateColumn(`${testId}-expired`, new Date(expirationDate))
            : dateColumn(`${testId}-expired`),
          createInfoCardColumnDefinition(
            <SingleStatusChip
              testId={`${testId}-info-chip`}
              {...retrieveChipAttributes({ t, status: certification.status })}
            />,
            undefined,
            120
          ),
        ],
        actionLists,
        showDisabledMenuTrigger: actionLists.length === 0,
        childItems: [],
      },
      certification,
    },
    footerProps: {},
  };
};

export const getCurrentAttributes = (
  t: TFunction,
  response: GetCertificationsResponse,
  onAction: CertificationActionCallback,
  type: CertificationType
) => {
  const disabled =
    type === "GOLD" && !goldCertificationAvailable(response.current);

  const current = getCurrentCertification(t, response, onAction)(type);
  const footerProps = current.certification
    ? {}
    : {
        buttonProps: {
          testId: `start-${type}`,
          label: labelForKey(t, type),
          onClick: () => onAction(ACTION_KEY[type]),
          disabled,
        },
      };

  return {
    current,
    footerProps,
  };
};

export const getCurrentCourses = (
  t: TFunction,
  response: GetCertificationsResponse,
  onAction: CertificationActionCallback,
  type: "EMPLOYEE" | "ELECTIVE"
) => {
  const courses = response.current.filter((cert) => cert.type === type);

  if (courses.length > 0) {
    return {
      current: courses.map((course) =>
        certificationToDataCard(course, t, false, onAction)
      ),
      footerProps: {},
    };
  } else {
    return {
      current: [emptyCard(t, type)],
      footerProps: {
        buttonProps: {
          testId: `start-${type}`,
          label: labelForKey(t, type),
          onClick: () => onAction(ACTION_KEY[type]),
          disabled: false,
        },
      },
    };
  }
};

const getCurrentCertification =
  (
    t: TFunction,
    response: GetCertificationsResponse,
    onAction: CertificationActionCallback
  ) =>
  (type: CertificationType) => {
    const credential = response.current.find((cert) => cert.type === type);
    if (!credential) return { card: emptyCard(t, type) };

    const disabled =
      type === "GOLD" && !goldCertificationAvailable(response.current);

    if (type === "CORE" && goldCertificationEarned(response.current)) {
      const credentialWithoutExpiration = {
        ...credential,
        expiresAt: undefined,
      };
      const card = certificationToDataCard(
        credentialWithoutExpiration,
        t,
        disabled,
        onAction
      );
      return { certification: credential, card };
    } else {
      const card = certificationToDataCard(credential, t, disabled, onAction);
      return { certification: credential, card };
    }
  };

const goldCertificationAvailable = (current: Certification[]) => {
  const coreCertification = firstForKind(current, "CORE");
  const goldCertification = firstForKind(current, "GOLD");

  const coreEarned =
    coreCertification &&
    ["EARNED", "EXPIRES_SOON"].includes(coreCertification.status);
  const goldEarned =
    goldCertification &&
    ["INVALID", "EXPIRED"].includes(goldCertification.status);

  return !!coreEarned || !!goldEarned;
};

const goldCertificationEarned = (current: Certification[]) => {
  const goldCertification = firstForKind(current, "GOLD");

  return (
    goldCertification &&
    ["EARNED", "EXPIRES_SOON"].includes(goldCertification.status)
  );
};

const iconForAvatar: Record<
  CertificationType | "ELECTIVE" | "EMPLOYEE",
  IconName
> = {
  CORE: "certification",
  GOLD: "certification",
  CREDENTIAL: "credential",
  ELECTIVE: "course",
  EMPLOYEE: "course",
};
const avatar = (
  type: CertificationType | "ELECTIVE" | "EMPLOYEE",
  color: AvatarProps["iconAvatarColor"] = "empty"
) => <Avatar icon={iconForAvatar[type]} iconAvatarColor={color} />;

const EMPTY_TRANSLATION_KEY: Record<
  CertificationType | "ELECTIVE" | "EMPLOYEE",
  TranslateKey
> = {
  CREDENTIAL: "training.credential.none",
  CORE: "training.core.none",
  GOLD: "training.gold.none",
  ELECTIVE: "training.elective.none",
  EMPLOYEE: "training.employee.none",
};

const emptyCard = (
  t: TFunction,
  type: CertificationType | "ELECTIVE" | "EMPLOYEE"
): NestedDataCardProps => {
  const testId = `certification-card-empty-${snakeCase(type)}`;

  return {
    id: testId,
    testId,
    noninteractive: true,
    disabled: true,
    contentColumns: [
      createDataCardInitialColumnDefinition({
        avatar: avatar(type, "neutral"),
        parentTestId: testId,
        heading: t(EMPTY_TRANSLATION_KEY[type]),
      }),
      { content: "" },
      { content: "" },
      { content: "" },
    ],
    childItems: [],
  };
};

const isNotCourse = (
  certification: Certification
): certification is Exclude<Certification, ElectiveWithType> => {
  if (["ELECTIVE", "EMPLOYEE"].includes(certification.type)) return false;

  return true;
};

const courseToDataCard = (
  certification: ElectiveWithType,
  t: TFunction,
  onAction: CertificationActionCallback
): NestedDataCardProps => {
  const { id, name, icon, completedAt, status } = certification;
  const testId = `certification-card-${snakeCase(name)}`;

  return {
    id,
    testId,
    contentColumns: [
      createDataCardInitialColumnDefinition(
        {
          avatar: icon ? (
            <IconContainer>
              <Icon name={icon} />
            </IconContainer>
          ) : (
            <Avatar icon={"course"} iconAvatarColor="empty" />
          ),
          parentTestId: testId,
          heading: name,
        },
        undefined,
        256
      ),
      dateColumn(`${testId}-completed-at`, completedAt),
      createInfoCardColumnDefinition(
        !["UNEARNED"].includes(status) ? (
          <SingleStatusChip
            testId={`${testId}-info-chip`}
            {...retrieveChipAttributes({ t, status: certification.status })}
          />
        ) : null,
        undefined,
        120
      ),
    ],
    actionLists: getCurrentActions(t, onAction, certification),
    childItems: [],
  };
};

const certificationToDataCard = (
  certification: Certification,
  t: TFunction,
  disabled: boolean,
  onAction: CertificationActionCallback
): NestedDataCardProps => {
  const testId = `certification-card-${snakeCase(certification.type)}`;
  if (!isNotCourse(certification))
    return courseToDataCard(certification, t, onAction);

  return {
    id: certification.type,
    testId,
    disabled,
    contentColumns: [
      createDataCardInitialColumnDefinition(
        {
          avatar: avatar(certification.type),
          parentTestId: testId,
          heading: certification.type,
          subtext: capitalize(certification.earnMethod),
        },
        undefined,
        256
      ),

      // Originally: dateColumn(`${testId}-earned`, certification.earnDate),
      // keeping as a reference for when we wire courses and electives
      dateColumn(`${testId}-earned`, new Date()),

      certification.type === "CREDENTIAL"
        ? { content: " " }
        : // Originally: dateColumn(`${testId}-expires`, certification.expiresAt),
          // keeping as a reference for when we wire courses and electives
          dateColumn(`${testId}-expires`, new Date()),
      createInfoCardColumnDefinition(
        !["UNEARNED"].includes(certification.status) ? (
          <SingleStatusChip
            testId={`${testId}-info-chip`}
            {...retrieveChipAttributes({ t, status: certification.status })}
          />
        ) : null,
        undefined,
        120
      ),
    ],
    actionLists: getCurrentActions(t, onAction, certification),
    childItems: [],
  };
};

export const retrieveDerivedStatesFromCards = ({
  currentCoreCertification,
  currentGoldCertification,
}: {
  currentCoreCertification: CurrentCertificationAttributes;
  currentGoldCertification: CurrentCertificationAttributes;
}) => {
  return {
    corePurchaseInActionMenu: currentCoreCertification.card.actionLists
      ?.flat()
      .some(({ id }) => id === "purchase-test"),
    coreCertificationEmpty:
      currentCoreCertification.card.id === "certification-card-empty-core",
    goldPurchaseInActionMenu: currentGoldCertification.card.actionLists
      ?.flat()
      .some(({ id }) => id === "purchase-test"),
  };
};
